import * as yup from 'yup'
import React, { useCallback, useEffect, useState } from 'react'
import useSWR, { SWRResponse } from 'swr'
import ReactQuill from 'react-quill'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import commonCodebase from 'common-recruspin-codebase'
import {
  AppBar,
  Autocomplete,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
  Menu,
  Box,
  Toolbar,
  IconButton,
} from '@mui/material'
import { IconCircleX } from '@tabler/icons-react'
import { useDropzone } from 'react-dropzone'
import { useFormik } from 'formik'
import { FormattedMessage } from 'react-intl'
import 'react-quill/dist/quill.snow.css'
import FileUploadArea from '../../../components/File/FileUpload'
import FormErrorsModal from '../../../components/FormErrorsModal'
import SuccessModal from '../../../components/SuccessModal'
import AlertModal from '../../../components/AlertModal'
import HeaderImageSelector from '../components/HeaderImageSelector'
import axiosInstance from '../../../utils/axiosInstance'
import Loading from '../../../components/Loading'
import { makeMultipartFormData } from '../../../utils/tools'
import { filterCampaigns } from '../../../utils/jobSearch'
import { validateFileCount, validateFileSize } from '../../../utils/fileValidation'
import {
  Iselect,
  allowedImageFileTypes,
  allowedPreviewFileTypes,
  allowedVideoFileTypes,
  allowedDocumentFileTypes,
  channels,
} from '../../../constants'
import { TresolvedAny } from '../../../types'
import { TunsolvedAny } from '../../../types/unsolved'
import { setTriggerOrdersRefetch } from '../../../store/slices/general'
import { useDispatch, useSelector } from '../../../store/store'
import { USER_ROLES } from '../../../constants/user'
import './style.scss'

const { statuses, types } = commonCodebase.orders

/** Initial values for a new order, e.g. taken from an imported ATS job. */
export interface CreateOrderPrefill {
  customer?: string
  jobName?: string
  place?: string
  adDescription?: string
}

interface Iprops {
  handleClose: () => void
  prefill?: CreateOrderPrefill
  // Called with the created order after a successful create. Errors thrown here are
  // logged and never reported as a failed create.
  onCreated?: (order: TresolvedAny) => void | Promise<void>
}

interface IdAndName {
  id: string
  name: string
  // Google campaigns only: the order the campaign is currently connected to, if any.
  orderId?: string | null
}

const CreateOrder: React.FC<Iprops> = ({ handleClose, prefill, onCreated }) => {
  const [previewFiles, setPreviewFiles] = useState<null | File[]>(null)
  const [imageAssetsFiles, setImageAssetsFiles] = useState<null | File[]>(null)
  const [videoAssets, setVideoAssets] = useState<null | File[]>(null)
  const [jobFile, setJobFile] = useState<File | null>(null)
  const [headerImageIndex, setHeaderImageIndex] = useState<number | null>(null)
  const [assignees, setAssignees] = useState([{ firstName: '', lastName: '', id: '' }])
  const [customers, setCustomers] = useState([{ name: '', id: '', emails: null }])
  const [formikErrors, setFormikErrors] = useState<string[]>([])
  const [errorsModalOpen, setErrorsModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [mutationError, setMutationError] = useState<null | [string]>(null)
  const [loadingMutation, setLoadingMutation] = useState(false)
  const [open, setOpen] = useState(false)
  const [isAllowed, setIsAllowed] = useState(false)
  const [error, setError] = useState('')

  const dispatch = useDispatch()
  const { triggerCustomersRefetch } = useSelector((state) => state.general)
  const { userInfo } = useSelector((state) => state.user)
  const isReseller = userInfo?.role === USER_ROLES.EXTERNAL_RESELLER.value
  const isResellerCustomer = userInfo?.role === USER_ROLES.EXTERNAL_RESELLER_CUSTOMER.value
  const isDirectCustomer = userInfo?.role === USER_ROLES.EXTERNAL_DIRECT_CUSTOMER.value
  const isCustomer = isDirectCustomer || isResellerCustomer || isReseller

  const { data: fetchedAssignees }: SWRResponse = useSWR(
    isCustomer ? null : '/app-users?internalUsers=true',
    axiosInstance
  )
  const { data: fetchedCustomers, mutate: refetch }: SWRResponse = useSWR(
    '/customers',
    axiosInstance
  )
  const { data: fetchedCampaigns, isLoading: loadingCampaigns }: SWRResponse = useSWR(
    '/campaignIdsAndNames',
    axiosInstance
  )
  const campaigns = fetchedCampaigns?.data.campaigns
  const { data: fetchedOriginalMetaCampaigns }: SWRResponse = useSWR(
    '/original-meta-campaigns-ids-and-names',
    axiosInstance
  )
  const originalMetaCampaigns = fetchedOriginalMetaCampaigns?.data.campaigns
  const { data: fetchedTiktokCampaigns, isLoading: loadingTiktokCampaigns }: SWRResponse = useSWR(
    '/tiktok-campaign-ids-and-names',
    axiosInstance
  )
  const tiktokCampaigns = fetchedTiktokCampaigns?.data.campaigns
  const { data: fetchedGoogleCampaigns, isLoading: loadingGoogleCampaigns }: SWRResponse = useSWR(
    '/google-campaign-ids-and-names',
    axiosInstance
  )
  const googleCampaigns = fetchedGoogleCampaigns?.data.campaigns

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [anchorElOriginal, setAnchorElOriginal] = React.useState<null | HTMLElement>(null)
  const openCampaign = Boolean(anchorEl)
  const openOriginalCampaign = Boolean(anchorElOriginal)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClickOriginal = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElOriginal(event.currentTarget)
  }

  const handleCampaignClose = () => {
    setAnchorEl(null)
  }
  const handleOriginalCampaignClose = () => {
    setAnchorElOriginal(null)
  }

  const redirectToCampaign = () => {
    handleCampaignClose()
    // Open the first selected campaign
    if (formik.values.metaCampaigns && formik.values.metaCampaigns.length > 0) {
      window.open(`/campaigns/${formik.values.metaCampaigns[0]}?legacy=1`, '_blank')
    }
  }
  const redirectToOriginalCampaign = () => {
    handleOriginalCampaignClose()
    window.open(`/analytics/dashboard/${formik.values.originalMetaCampaign}`, '_blank')
  }

  const checkFileFormat = (
    acceptedFiles: File[],
    allowedTypes: string[],
    errorMessage: string,
    maxCount?: number
  ) => {
    const incomingTypes = acceptedFiles.map((file) => file.type)
    const isAllowed = incomingTypes.every((type) => allowedTypes.includes(type))
    if (!isAllowed) {
      setError(errorMessage)
      setIsAllowed(isAllowed)
      setOpen(true)
      throw new Error(errorMessage)
    }
    const countError = validateFileCount(acceptedFiles, maxCount)
    if (countError) {
      setError(countError)
      setIsAllowed(false)
      setOpen(true)
      throw new Error(countError)
    }
    const sizeError = validateFileSize(acceptedFiles)
    if (sizeError) {
      setError(sizeError)
      setIsAllowed(false)
      setOpen(true)
      throw new Error(sizeError)
    }
  }

  const handleCloseAlert = () => {
    setOpen((prev) => !prev)
  }
  const onDropPreviewFiles = useCallback((acceptedFiles: File[]) => {
    checkFileFormat(
      acceptedFiles,
      Object.values(allowedPreviewFileTypes.file),
      'File format is not supported',
      10
    )
    setPreviewFiles(acceptedFiles)
  }, [])
  const onDropImageAssetsFiles = useCallback((acceptedFiles: File[]) => {
    checkFileFormat(
      acceptedFiles,
      Object.values(allowedImageFileTypes.image),
      'File format is not supported',
      5
    )
    setImageAssetsFiles(acceptedFiles)
  }, [])
  const onDropVideoAssetsFiles = useCallback((acceptedFiles: File[]) => {
    checkFileFormat(
      acceptedFiles,
      Object.values(allowedVideoFileTypes.video),
      'File format is not supported',
      4
    )
    setVideoAssets(acceptedFiles)
  }, [])

  const onDropJobFile = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      const allowedTypes = Object.values(allowedDocumentFileTypes.document)
      if (!allowedTypes.includes(file.type)) {
        setError('Only PDF and Word documents are allowed for job file')
        setIsAllowed(false)
        setOpen(true)
        return
      }
      const sizeError = validateFileSize([file])
      if (sizeError) {
        setError(sizeError)
        setIsAllowed(false)
        setOpen(true)
        return
      }
      setJobFile(file)
    }
  }, [])

  const {
    acceptedFiles: acceptedFilesPreviews,
    getRootProps: getRootPropsPreviews,
    getInputProps: getInputPropsPreviews,
    isDragActive: isDragActivePreviews,
  } = useDropzone({
    onDrop: onDropPreviewFiles,
    maxFiles: 10,
  })

  const {
    acceptedFiles: acceptedJobFiles,
    getRootProps: getRootPropsJobFile,
    getInputProps: getInputPropsJobFile,
    isDragActive: isDragActiveJobFile,
  } = useDropzone({
    onDrop: onDropJobFile,
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  })

  const {
    acceptedFiles: acceptedFilesImageAssets,
    getRootProps: getRootPropsImageAssets,
    getInputProps: getInputPropsImageAssets,
    isDragActive: isDragActiveImageAssets,
  } = useDropzone({
    onDrop: onDropImageAssetsFiles,
    maxFiles: 5,
  })

  const {
    acceptedFiles: acceptedFilesVideoAssets,
    getRootProps: getRootPropsVideoAssets,
    getInputProps: getInputPropsVideoAssets,
    isDragActive: isDragActiveVideoAssets,
  } = useDropzone({
    onDrop: onDropVideoAssetsFiles,
    maxFiles: 4,
  })

  const validationSchema = yup.object({
    customer: yup
      .string()
      .required()
      .oneOf(customers.map((i) => i.id))
      .default(customers.map((i) => i.id)[0]),
    orderType: yup.string().required(),
    place: yup.string().required().min(2, 'Place should be of minimum 2 characters length'),
    assignee: yup
      .string()
      .oneOf(assignees.map((i) => i.id))
      .default(assignees.map((i) => i.firstName)[0])
      .required(),
    jobName: yup.string().required().min(2, 'Job title should be of minimum 2 characters length'),
    status: yup
      .string()
      .required()
      .oneOf(statuses.map((i) => i.value))
      .default(''),
    channel: yup.array().required(),
    jobAdLink: yup
      .string()
      .nullable()
      .test('jobAdLink-or-jobFile', 'Either Job Ad Link or Job File is required', function () {
        // This validation will be handled in handleSubmit where we have access to jobFile state
        return true
      }),
    applyUrl: yup.string().nullable(),
    externalImportId: yup.string().nullable(),
    metaCampaigns: yup.array().of(yup.string()).nullable(),
    originalMetaCampaign: yup
      .string()
      .oneOf(
        originalMetaCampaigns
          ? originalMetaCampaigns?.map((i: { id: string; name: string }) => i.id)
          : []
      )
      .nullable(),
    tiktokCampaign: yup
      .string()
      .oneOf(
        tiktokCampaigns ? tiktokCampaigns?.map((i: { id: string; name: string }) => i?.id) : []
      )
      .nullable(),
    googleCampaigns: yup.array().of(yup.string()).nullable(),
    keywords: yup.string().nullable(),
    adDescription: yup.string().nullable(),
    comments: yup.string().max(1000, 'Maximum 1000 Charachters allowed'),
    budget: isCustomer ? yup.number().min(0).optional() : yup.number().min(0).required(),
    price:
      isResellerCustomer || isDirectCustomer
        ? yup.number().min(150, 'Minimum price is €150').required()
        : yup.number().min(0).required(),
    runtime: yup.number().min(0).required(),
    previews: yup.object().nullable(),
    imageAssets: yup.object().nullable(),
    videoAssets: yup.object().nullable(),
  })

  const formik = useFormik({
    initialValues: {
      customer: prefill?.customer || customers.map((i) => i.id)[0],
      assignee: isCustomer
        ? userInfo?.id || assignees.map((i) => i.id)[0]
        : assignees.map((i) => i?.id)[0],
      jobName: prefill?.jobName || '',
      status: isCustomer ? 'todo' : statuses.map((i) => i.value)[0],
      place: prefill?.place || '',
      channel: [],
      orderType: '',
      jobAdLink: '',
      applyUrl: '',
      externalImportId: '',
      metaCampaigns: [] as string[],
      originalMetaCampaign: undefined,
      tiktokCampaign: undefined,
      googleCampaigns: [] as string[],
      keywords: '',
      adDescription: prefill?.adDescription || '',
      comments: '',
      budget: '',
      price: '',
      runtime: '',
      previews: undefined,
      previewsVideo: undefined,
      previewsAudio: undefined,
      imageAssets: undefined,
      videoAssets: undefined,
    },
    validationSchema,
    onSubmit: async (values) => {
      const newFormData: TunsolvedAny = {
        jobName: values.jobName,
        jobType: values.orderType,
        place: values.place,
        channel: values.channel,
        budget: values.budget ? String(values.budget) : null,
        price: String(values.price),
        runtime: values.runtime,
        jobAdLink: values.jobAdLink,
        applyUrl: values.applyUrl,
        externalImportId: values.externalImportId,
        keywords: values.keywords,
        adDescription: values.adDescription,
        comments: values.comments,
        status: values.status,
        assignee: {
          connect: { id: values.assignee },
        },
        customer: {
          connect: { id: values.customer },
        },
        previews: values.previews,
        previewsAudio: values.previewsAudio,
        previewsVideo: values.previewsVideo,
        imageAssets: values.imageAssets,
        videoAssets: values.videoAssets,
      }

      if (values.metaCampaigns && values.metaCampaigns.length > 0) {
        newFormData.metaCampaign = {
          connect: values.metaCampaigns.map((id: string) => ({ id })),
        }
      } else {
        newFormData.metaCampaign = undefined
      }

      if (values.originalMetaCampaign) {
        newFormData.originalMetaCampaign = {
          connect: { id: values.originalMetaCampaign },
        }
      } else {
        newFormData.originalMetaCampaign = undefined
      }

      if (values.tiktokCampaign) {
        newFormData.tiktokCampaign = {
          connect: { id: values.tiktokCampaign },
        }
      } else {
        newFormData.tiktokCampaign = undefined
      }

      if (values.googleCampaigns && values.googleCampaigns.length > 0) {
        newFormData.googleCampaigns = {
          connect: values.googleCampaigns.map((id: string) => ({ id })),
        }
      } else {
        newFormData.googleCampaigns = undefined
      }

      // Handle jobFile upload - jobFile is a single relationship (many: false)
      if (jobFile) {
        newFormData.jobFile = {
          create: {
            name: jobFile.name,
            file: {
              upload: jobFile,
            },
          },
        }
      }

      const mapFileFields = [
        { name: 'previews', source: 'image' },
        { name: 'previewsAudio', source: 'image' },
        { name: 'previewsVideo', source: 'image' },
        { name: 'imageAssets', source: 'image' },
        { name: 'videoAssets', source: 'file' },
        { name: 'jobFile', source: 'file' },
      ]

      const formData = makeMultipartFormData(mapFileFields, newFormData)

      // Include header image index if selected
      if (headerImageIndex !== null) {
        formData.append('headerImageIndex', String(headerImageIndex))
      }

      try {
        setLoadingMutation(true)
        const response = await axiosInstance.post('/orders/create', formData, {
          headers: {
            'Content-Type': 'multipart/form-data;',
          },
        })
        setSuccessModalOpen(true)
        dispatch(setTriggerOrdersRefetch(true))
        if (onCreated) {
          // The order exists at this point; a failing follow-up must not surface as
          // "Failed to create order".
          try {
            await onCreated(response.data)
          } catch (followUpError) {
            console.error('Error after creating order:', followUpError)
          }
        }
      } catch (error: TresolvedAny) {
        const err = error as TresolvedAny
        console.error('Error creating order:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: err.message,
          fullError: err,
        })

        // Extract meaningful error message
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.response?.statusText ||
          err.message ||
          'Failed to create order'

        setMutationError([errorMessage])
      } finally {
        setLoadingMutation(false)
      }
    },
  })

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    // Validate that at least one of jobAdLink or jobFile is provided
    if (!formik.values.jobAdLink && !jobFile) {
      setFormikErrors(['Please provide either a Job Ad Link or upload a Job File'])
      setErrorsModalOpen(true)
      return
    }

    const errors = Object.values(formik.errors)
    if (errors.length) {
      setErrorsModalOpen(true)
      setFormikErrors(errors as string[])
      return
    }

    await handleFormFiles()

    formik.handleSubmit()

    async function handleFormFiles() {
      const mapFile = (file: File, uploadKey: string) => ({
        name: file.name,
        [uploadKey]: {
          upload: file,
        },
      })

      const handleFiles = async (field: string, files: File[] | null, uploadKey: string) => {
        if (files) {
          const mappedFiles = files.map((file: File) => mapFile(file, uploadKey))
          await formik.setFieldValue(field, { create: mappedFiles })
        }
      }

      const handlePreviewFiles = async (files: File[] | null, uploadKey: string) => {
        if (files?.length) {
          interface MapFileInterface {
            name: string
          }
          const audio: MapFileInterface[] = []
          const video: MapFileInterface[] = []
          const image: MapFileInterface[] = []

          files.forEach((file: File) => {
            if (file.type.includes('audio')) {
              audio.push(mapFile(file, uploadKey))
            }
            if (file.type.includes('video')) {
              video.push(mapFile(file, uploadKey))
            }
            if (file.type.includes('image')) {
              image.push(mapFile(file, uploadKey))
            }
          })

          await Promise.all([
            formik.setFieldValue('previewsAudio', { create: audio.length ? audio : [] }),
            formik.setFieldValue('previewsVideo', { create: video.length ? video : [] }),
            formik.setFieldValue('previews', { create: image.length ? image : [] }),
          ])
        }
      }

      await Promise.all([
        handlePreviewFiles(previewFiles, 'image'),
        handleFiles('imageAssets', imageAssetsFiles, 'image'),
        handleFiles('videoAssets', videoAssets, 'file'),
      ])
    }
  }

  // Auto-select customer as assignee when assignees are loaded
  useEffect(() => {
    if (
      isCustomer &&
      userInfo?.id &&
      assignees.length > 0 &&
      formik.values.assignee !== userInfo.id
    ) {
      // Always set the customer as the assignee
      formik.setFieldValue('assignee', userInfo.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomer, userInfo?.id, assignees.length])

  useEffect(() => {
    if (triggerCustomersRefetch) {
      refetch()
      dispatch(setTriggerOrdersRefetch(false))
    }
  }, [dispatch, refetch, triggerCustomersRefetch])

  useEffect(() => {
    let assigneesList: { id: string; firstName: string; lastName: string }[] = []

    if (isCustomer && userInfo) {
      // For customers (resellers + direct + reseller customers), always add themselves as the only assignee option
      assigneesList = [
        {
          id: userInfo.id,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
        },
      ]
    } else if (fetchedAssignees?.data?.appUsers) {
      // For non-customers (internal users), use the fetched internal users
      assigneesList = fetchedAssignees.data.appUsers
    }

    if (assigneesList.length > 0) {
      setAssignees(assigneesList)
    }

    if (fetchedCustomers?.data?.customers) {
      setCustomers(fetchedCustomers.data.customers)
    }
  }, [fetchedAssignees, fetchedCustomers, isCustomer, userInfo])

  useEffect(() => {
    if (mutationError) {
      setFormikErrors(mutationError)
      setErrorsModalOpen(true)
    }
  }, [mutationError])

  if (loadingMutation) return <Loading />

  return (
    <div className="CreateOrder">
      <form onSubmit={(e) => handleSubmit(e)}>
        <AppBar sx={{ position: 'sticky' }} color="default">
          {AppBarToolbar()}
        </AppBar>
        {FormContainer()}
        <FormErrorsModal
          errorsModalOpen={errorsModalOpen}
          formikErrors={formikErrors}
          setErrorsModalOpen={setErrorsModalOpen}
        />
        <SuccessModal
          successModalOpen={successModalOpen}
          handleClose={handleClose}
          setSuccessModalOpen={setSuccessModalOpen}
          message={<FormattedMessage id="created-successfully" />}
        />
      </form>
      {!isAllowed && (
        <AlertModal message={error} isAlert alertModalOpen={open} handleClose={handleCloseAlert} />
      )}
    </div>
  )

  function FormContainer() {
    return (
      <div className="formContainer">
        <Container maxWidth="md" className="customerDetails subFormContainer">
          <div className="header">
            <FormattedMessage id="customer-details" />
          </div>
          <Grid container className="content">
            <Autocomplete
              fullWidth
              id="customer"
              options={customers}
              getOptionLabel={(option) => option.name}
              value={customers.find((customer) => customer.id === formik.values.customer) || null}
              onChange={(event, newValue) => {
                formik.setFieldValue('customer', newValue ? newValue.id : '')
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={<FormattedMessage id="customer" />}
                  variant="outlined"
                  error={formik.touched.customer && Boolean(formik.errors.customer)}
                />
              )}
            />
            <FormControl fullWidth>
              <Autocomplete
                options={assignees}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                value={assignees.find((assignee) => assignee.id === formik.values.assignee) || null}
                onChange={(event, newValue) => {
                  if (!isCustomer) {
                    formik.setFieldValue('assignee', newValue ? newValue.id : '')
                  }
                }}
                disabled={isCustomer}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id="assignee" />}
                    variant="outlined"
                    error={formik.touched.assignee && Boolean(formik.errors.assignee)}
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Container>
        <Container maxWidth="md" className="jobDetails subFormContainer">
          <div className="header">
            <FormattedMessage id="job-details" />
          </div>
          <Grid container className="content">
            <TextField
              fullWidth
              label={<FormattedMessage id="job-title" />}
              id="jobName"
              name="jobName"
              placeholder="Job title"
              type="jobName"
              value={formik.values.jobName}
              onChange={formik.handleChange}
              error={formik.touched.jobName && Boolean(formik.errors.jobName)}
            />
            <FormControl fullWidth>
              <InputLabel id="status">
                <FormattedMessage id="status" />
              </InputLabel>
              <Select
                fullWidth
                labelId="status"
                id="status"
                name="status"
                value={formik.values.status}
                label={<FormattedMessage id="status" />}
                onChange={isCustomer ? undefined : formik.handleChange}
                disabled={isCustomer}
              >
                {statuses.map((i: Iselect) => (
                  <MenuItem key={i.value} value={i.value}>
                    {i.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="orderType">
                <FormattedMessage id="order-type" />
              </InputLabel>
              <Select
                fullWidth
                labelId="orderType"
                id="orderType"
                name="orderType"
                value={formik.values.orderType}
                label={<FormattedMessage id="order-type" />}
                onChange={formik.handleChange}
              >
                {types.map((i: Iselect) => (
                  <MenuItem key={i.value} value={i.value}>
                    {i.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={<FormattedMessage id="place" />}
              id="place"
              name="place"
              placeholder="Place"
              type="place"
              value={formik.values.place}
              onChange={formik.handleChange}
              error={formik.touched.place && Boolean(formik.errors.place)}
            />
            <FormControl fullWidth>
              <InputLabel id="channel">
                <FormattedMessage id="portal" />
              </InputLabel>
              <Select
                fullWidth
                multiple
                labelId="channel"
                id="channel"
                name="channel"
                value={formik.values.channel}
                label={<FormattedMessage id="portal" />}
                onChange={formik.handleChange}
              >
                {channels.map((i: Iselect) => (
                  <MenuItem key={i.value} value={i.value}>
                    <Chip variant="outlined" key={i.value} label={i.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={<FormattedMessage id="job-ad-link" />}
              id="jobAdLink"
              name="jobAdLink"
              placeholder="Link to job ad"
              type="text"
              value={formik.values.jobAdLink}
              onChange={formik.handleChange}
              error={formik.touched.jobAdLink && Boolean(formik.errors.jobAdLink)}
              helperText={
                formik.touched.jobAdLink && formik.errors.jobAdLink
                  ? String(formik.errors.jobAdLink)
                  : undefined
              }
            />
            <TextField
              fullWidth
              label={<FormattedMessage id="apply-link" />}
              id="applyUrl"
              name="applyUrl"
              placeholder="Link to apply"
              type="text"
              value={formik.values.applyUrl}
              onChange={formik.handleChange}
              error={formik.touched.applyUrl && Boolean(formik.errors.applyUrl)}
              helperText={
                formik.touched.applyUrl && formik.errors.applyUrl
                  ? String(formik.errors.applyUrl)
                  : undefined
              }
            />
            <Box sx={{ width: '100%', mb: 2 }}>
              <InputLabel sx={{ mb: 2, fontWeight: 'medium', fontSize: '0.875rem' }}>
                Job File (PDF or Word){' '}
                {!jobFile && !formik.values.jobAdLink && <span style={{ color: 'red' }}>*</span>}
              </InputLabel>
              <FileUploadArea
                getRootProps={getRootPropsJobFile}
                getInputProps={getInputPropsJobFile}
                acceptedFiles={acceptedJobFiles}
                isDragActive={isDragActiveJobFile}
                title="Job File"
                withResults
                supportedFormats={['PDF', 'DOC', 'DOCX']}
              />
            </Box>
            {!isCustomer && (
              <TextField
                fullWidth
                label={<FormattedMessage id="external-import-id" />}
                id="externalImportId"
                name="externalImportId"
                placeholder="External import id"
                type="text"
                value={formik.values.externalImportId}
                onChange={formik.handleChange}
                error={formik.touched.externalImportId && Boolean(formik.errors.externalImportId)}
              />
            )}

            <TextField
              fullWidth
              label={<FormattedMessage id="keywords" />}
              id="keywords"
              name="keywords"
              placeholder="Keywords"
              type="text"
              value={formik.values.keywords}
              onChange={formik.handleChange}
              error={formik.touched.keywords && Boolean(formik.errors.keywords)}
            />

            <Box sx={{ width: '100%', mb: 2 }}>
              <InputLabel
                sx={{
                  mb: 1,
                  color:
                    formik.touched.adDescription && formik.errors.adDescription
                      ? 'error.main'
                      : 'text.primary',
                  fontWeight: 'medium',
                  fontSize: '0.875rem',
                }}
              >
                Ad Description
              </InputLabel>
              <ReactQuill
                value={formik.values.adDescription || ''}
                onChange={(value) => formik.setFieldValue('adDescription', value)}
                placeholder="Enter ad description..."
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link'],
                    ['clean'],
                  ],
                }}
                formats={[
                  'header',
                  'bold',
                  'italic',
                  'underline',
                  'strike',
                  'list',
                  'bullet',
                  'link',
                ]}
                style={{
                  width: '100%',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border:
                    formik.touched.adDescription && formik.errors.adDescription
                      ? '2px solid #d32f2f'
                      : '1px solid rgba(0, 0, 0, 0.23)',
                  fontSize: '1rem',
                }}
              />
              {formik.touched.adDescription && formik.errors.adDescription && (
                <Box sx={{ mt: 1, color: 'error.main', fontSize: '0.75rem', marginLeft: '14px' }}>
                  {typeof formik.errors.adDescription === 'string'
                    ? formik.errors.adDescription
                    : 'Invalid ad description format'}
                </Box>
              )}
            </Box>

            {(!isCustomer || isDirectCustomer || (campaigns && campaigns.length > 0)) &&
              (loadingCampaigns ? (
                <Loading />
              ) : (
                <div className="campaignRow">
                  <Autocomplete
                    fullWidth
                    multiple
                    options={campaigns || []}
                    getOptionLabel={(option: IdAndName) => option.name}
                    getOptionKey={(option: IdAndName) => option.id}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, { inputValue }) =>
                      filterCampaigns(options, inputValue)
                    }
                    value={
                      campaigns?.filter((c: IdAndName) =>
                        formik.values.metaCampaigns?.includes(c.id)
                      ) || []
                    }
                    onChange={(e, value) => {
                      formik.setFieldValue(
                        'metaCampaigns',
                        value.map((v) => v.id)
                      )
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<FormattedMessage id="leadgenform" />}
                        id="metaCampaigns"
                        name="metaCampaigns"
                        placeholder="Leadgenform"
                        type="text"
                        error={formik.touched.metaCampaigns && Boolean(formik.errors.metaCampaigns)}
                      />
                    )}
                  />
                  <div>
                    <Tooltip title="options" className="icon">
                      <IconButton
                        id="basic-button"
                        aria-controls={open ? 'basic-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        disabled={!formik.values.metaCampaigns?.length}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={openCampaign}
                      onClose={handleCampaignClose}
                      MenuListProps={{
                        'aria-labelledby': 'basic-button',
                      }}
                    >
                      <MenuItem onClick={() => redirectToCampaign()}>
                        <FormattedMessage id="Open Campaign" />
                      </MenuItem>
                    </Menu>
                  </div>
                </div>
              ))}

            {!isCustomer &&
              (loadingCampaigns ? (
                <Loading />
              ) : (
                <div className="campaignRow">
                  <Autocomplete
                    fullWidth
                    options={originalMetaCampaigns}
                    getOptionLabel={(option: IdAndName) => option.name}
                    getOptionKey={(option: IdAndName) => option.id}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, { inputValue }) =>
                      filterCampaigns(options, inputValue)
                    }
                    onChange={(e, value) => {
                      formik.setFieldValue('originalMetaCampaign', value?.id)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<FormattedMessage id="original-meta-campaign" />}
                        id="originalMetaCampaign"
                        name="originalMetaCampaign"
                        placeholder="Original meta campaign"
                        type="text"
                        value={formik.values.originalMetaCampaign}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.originalMetaCampaign &&
                          Boolean(formik.errors.originalMetaCampaign)
                        }
                      />
                    )}
                  />
                  <div>
                    <Tooltip title="options" className="icon">
                      <IconButton
                        id="original-campaign-button"
                        aria-controls={openOriginalCampaign ? 'original-campaign-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={openOriginalCampaign ? 'true' : undefined}
                        onClick={handleClickOriginal}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      id="original-campaign-menu"
                      anchorEl={anchorElOriginal}
                      open={openOriginalCampaign}
                      onClose={handleOriginalCampaignClose}
                      MenuListProps={{
                        'aria-labelledby': 'original-campaign-button',
                      }}
                    >
                      <MenuItem onClick={() => redirectToOriginalCampaign()}>
                        <FormattedMessage id="Open Campaign" />
                      </MenuItem>
                    </Menu>
                  </div>
                </div>
              ))}

            {!isCustomer &&
              (loadingTiktokCampaigns ? (
                <Loading />
              ) : (
                <div className="campaignRow">
                  <Autocomplete
                    fullWidth
                    options={tiktokCampaigns}
                    getOptionLabel={(option: IdAndName) => option.name}
                    onChange={(e, value) => {
                      formik.setFieldValue('tiktokCampaign', value?.id)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="TikTok Campaign"
                        id="tiktokCampaign"
                        name="tiktokCampaign"
                        placeholder="TikTok Campaign"
                        type="text"
                        value={formik.values.tiktokCampaign}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.tiktokCampaign && Boolean(formik.errors.tiktokCampaign)
                        }
                      />
                    )}
                  />
                </div>
              ))}

            {!isCustomer &&
              (loadingGoogleCampaigns ? (
                <Loading />
              ) : (
                <div className="campaignRow">
                  <Autocomplete
                    fullWidth
                    multiple
                    options={googleCampaigns || []}
                    // A campaign belongs to one order: selecting one that another order
                    // already has moves it here, so say so before it is picked.
                    getOptionLabel={(option: IdAndName) =>
                      option.orderId ? `${option.name} (on another order)` : option.name
                    }
                    getOptionKey={(option: IdAndName) => option.id}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={
                      googleCampaigns?.filter((c: IdAndName) =>
                        formik.values.googleCampaigns?.includes(c.id)
                      ) || []
                    }
                    onChange={(e, value) => {
                      formik.setFieldValue(
                        'googleCampaigns',
                        value.map((v) => v.id)
                      )
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Google Campaigns"
                        id="googleCampaigns"
                        name="googleCampaigns"
                        placeholder="Google Campaigns"
                        type="text"
                        error={
                          formik.touched.googleCampaigns && Boolean(formik.errors.googleCampaigns)
                        }
                      />
                    )}
                  />
                </div>
              ))}
          </Grid>
        </Container>
        <Container maxWidth="md" className="comments subFormContainer">
          <div className="header">
            <FormattedMessage id="comments" />
          </div>
          <Grid container className="content">
            <TextField
              fullWidth
              id="comments"
              name="comments"
              placeholder="comments"
              rows={3}
              multiline
              type="text"
              value={formik.values.comments}
              onChange={formik.handleChange}
              error={formik.touched.comments && Boolean(formik.errors.comments)}
            />
          </Grid>
        </Container>
        <Container maxWidth="md" className="budget subFormContainer">
          <div className="header">
            <FormattedMessage id="budget" />
          </div>
          <Grid container className="content">
            {!isCustomer && (
              <TextField
                fullWidth
                label={<FormattedMessage id="budget" />}
                id="budget"
                name="budget"
                placeholder="budget"
                type="number"
                value={formik.values.budget}
                onChange={formik.handleChange}
                error={formik.touched.budget && Boolean(formik.errors.budget)}
              />
            )}
            <TextField
              fullWidth
              label={<FormattedMessage id="price" />}
              id="price"
              name="price"
              placeholder="Price"
              type="number"
              value={formik.values.price}
              onChange={formik.handleChange}
              error={formik.touched.price && Boolean(formik.errors.price)}
              helperText={
                formik.touched.price && formik.errors.price
                  ? formik.errors.price
                  : isResellerCustomer || isDirectCustomer
                  ? 'Minimum price: €150'
                  : undefined
              }
            />
            <TextField
              fullWidth
              label={<FormattedMessage id="runtime" />}
              id="runtime"
              name="runtime"
              placeholder="runtime"
              type="number"
              value={formik.values.runtime}
              onChange={formik.handleChange}
              error={formik.touched.runtime && Boolean(formik.errors.runtime)}
              // helperText={formik.touched.name && formik.errors.name}
              InputProps={{
                endAdornment: <InputAdornment position="start">days</InputAdornment>,
              }}
            />
          </Grid>
        </Container>
        {!isCustomer && (
          <Container maxWidth="md" className="files subFormContainer">
            <div className="header">
              <FormattedMessage id="previews" />
            </div>
            <div className="content">
              <FileUploadArea
                title="Previews"
                getInputProps={getInputPropsPreviews}
                getRootProps={getRootPropsPreviews}
                acceptedFiles={acceptedFilesPreviews}
                isDragActive={isDragActivePreviews}
                withResults
                supportedFormats={Object.keys(allowedPreviewFileTypes.file)}
              />
            </div>
          </Container>
        )}
        <Container maxWidth="md" className="files subFormContainer">
          <div className="header">
            <FormattedMessage id="customer-image-assets" />
          </div>
          <div className="content">
            <FileUploadArea
              title="Customer image assets"
              getInputProps={getInputPropsImageAssets}
              getRootProps={getRootPropsImageAssets}
              acceptedFiles={acceptedFilesImageAssets}
              isDragActive={isDragActiveImageAssets}
              withResults
              supportedFormats={Object.keys(allowedImageFileTypes.image)}
            />

            {/* Header Image Selection */}
            {acceptedFilesImageAssets && acceptedFilesImageAssets.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  <FormattedMessage id="select-header-image" />
                </Typography>
                <Box>
                  {acceptedFilesImageAssets.map((file, index) => (
                    <HeaderImageSelector
                      key={`${file.name}-${index}`}
                      imageAssetId={String(index)}
                      imageAssetName={file.name}
                      imageAssetUrl={URL.createObjectURL(file)}
                      isHeader={headerImageIndex === index}
                      onToggleHeader={() =>
                        setHeaderImageIndex(headerImageIndex === index ? null : index)
                      }
                    />
                  ))}
                </Box>
              </Box>
            )}
          </div>
        </Container>
        <Container maxWidth="md" className="files subFormContainer">
          <div className="header">
            <FormattedMessage id="video-assets" />
          </div>
          <div className="content">
            <FileUploadArea
              title="Video assets"
              getInputProps={getInputPropsVideoAssets}
              getRootProps={getRootPropsVideoAssets}
              acceptedFiles={acceptedFilesVideoAssets}
              isDragActive={isDragActiveVideoAssets}
              withResults
              supportedFormats={Object.keys(allowedVideoFileTypes.video)}
            />
          </div>
        </Container>
      </div>
    )
  }

  function AppBarToolbar() {
    return (
      <Toolbar>
        <IconButton
          size="medium"
          edge="start"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
        >
          <IconCircleX size="50px" color="gray" />
        </IconButton>
        <Typography sx={{ ml: 3, flex: 1, textAlign: 'center' }} variant="h3" component="div">
          <FormattedMessage id="create-new-order" />
        </Typography>
        <Button variant="contained" color="secondary" autoFocus size="large" type="submit">
          <FormattedMessage id="create" />
        </Button>
      </Toolbar>
    )
  }
}

export default CreateOrder
